import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, BarChart3, Eye } from 'lucide-react';
import { useClasseContext } from '../../context/ClasseContext';
import { classeHorsBarrette, phraseHorsBarrette } from '../../utils/groupesDeBesoin';
import { useUser } from '../../context/UserContext';
import { useEvalNat } from '../../hooks/fetch_api_hook/useEvalNat';
import { EVAL_NAT_CONFIG } from '../VueEvalNat/EvalNat/evaluationConfig';
import ResultatEvalNatByEleve from '../VueEvalNat/EvalNat/ResultatEvalNatByEleve';
import { buildProgressionEvalNat } from '../../utils/evalnatfunction';
import { formatLettresDate } from '../../utils/ClasseEvaluations';
import Loading from '../general/Loading';

/**
 * Onglet « Évaluations nationales » de la fiche élève (3.4).
 *
 * Affiche, POUR UN élève, ses résultats à l'évaluation nationale officielle
 * (niveaux par domaine : Très satisfaisant / Satisfaisant / Fragile / À besoins) —
 * l'autre lentille du suivi, complémentaire du /6 en classe (DetailEleve), sans
 * conversion entre les deux échelles.
 *
 * Données : `useEvalNat` (mêmes règles qu'EvalNatLoader) → on garde les évals
 * « Evaluation Nationale » où l'élève a des réponses. Niveaux par domaine
 * dérivés d'EVAL_NAT_CONFIG (outputName → displayName) ; détail question par
 * question via le modal existant ResultatEvalNatByEleve.
 */

// Niveau → couleur (aligné sur getColor d'evalnatfunction).
const COULEUR_NIVEAU = {
    'Très Satisfaisant': '#228B22',
    'Satisfaisant': '#86efac',
    'Fragile': '#fbbf24',
    'À besoins': '#ef4444',
    'A besoins': '#ef4444',
    'A besoin': '#ef4444',
};
const couleurNiveau = (n) => COULEUR_NIVEAU[(n || '').trim()] || '#cbd5e1';
// Texte foncé sur les fonds clairs (Satisfaisant/Fragile)
const texteFonce = (n) => ['Satisfaisant', 'Fragile'].includes((n || '').trim());

// État d'une cellule → couleur (réussi / partiel / raté / non évalué).
const COULEUR_CARRE = { reussi: 'bg-emerald-500', partiel: 'bg-amber-500', rate: 'bg-red-500', vide: 'bg-slate-200' };

const matiereKey = (m) => (m === 'français' || m === 'Français' ? 'Français' : 'maths');

// Niveaux par domaine d'une fiche (string-valeurs dans reponses, mappées via config).
const domainesDe = (ev, eleveEntry) => {
    const niveau = (ev.classe_nom || '').toString()[0];
    const cfg = EVAL_NAT_CONFIG[matiereKey(ev.matiere)]?.[niveau]?.columns || {};
    return Object.values(cfg)
        .map((c) => ({ label: c.displayName, niveau: eleveEntry.reponses?.[c.outputName] }))
        .filter((d) => typeof d.niveau === 'string' && d.niveau.trim());
};

const EleveEvalNat = ({ selectedEleveId, nomEleve, onBack }) => {
    const { idclasse } = useParams();
    const { classeNom, classeDetail } = useClasseContext();
    const { matiereFilter } = useUser();
    const [detail, setDetail] = useState(null); // { ev, eleveEntry } → modal

    // Ouverte depuis un groupe de besoin, la fiche d'un membre parti hors de la barrette le dit
    // (même phrase que la vue du groupe) au lieu d'un vide. Null pour une classe ou un demi-groupe.
    const classeHors = useMemo(() => {
        const membre = (classeDetail?.eleves || []).find((e) => String(e.id) === String(selectedEleveId));
        return membre ? classeHorsBarrette(membre, classeDetail) : null;
    }, [classeDetail, selectedEleveId]);

    // Même gating qu'EvalNatLoader : fetch par classe (classes_pk) en mode classe.
    const { data, isLoading } = useEvalNat(
        null,
        parseInt(idclasse, 10),
        0,
        classeNom === '' && 'not-enabled',
        matiereFilter,
    );

    // Évals nationales officielles où CET élève a des réponses
    const fiches = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data
            .filter((ev) => (ev.titre || '').includes('Evaluation Nationale'))
            .map((ev) => {
                const eleveEntry = (ev.eleves_reponses || []).find(
                    (e) => String(e.id_eleve) === String(selectedEleveId),
                );
                return eleveEntry ? { ev, eleveEntry } : null;
            })
            .filter(Boolean);
    }, [data, selectedEleveId]);

    // Bilans QCM de suivi (mêmes données que useEvalNat, titre ≠ « Evaluation Nationale »).
    const bilans = useMemo(
        () => (Array.isArray(data) ? data.filter((ev) => !(ev.titre || '').includes('Evaluation Nationale')) : []),
        [data],
    );

    // Grille de progression par fiche nationale (National + bilans de même matière, chronologiques).
    const progressionParFiche = useMemo(
        () =>
            new Map(
                fiches.map(({ ev, eleveEntry }) => {
                    const bilansFiche = bilans
                        .map((b) => ({
                            ev: b,
                            eleveEntry: (b.eleves_reponses || []).find(
                                (e) => String(e.id_eleve) === String(selectedEleveId),
                            ),
                        }))
                        .filter((x) => x.eleveEntry && x.ev.matiere === ev.matiere)
                        .sort((a, b) => new Date(a.ev.date || 0) - new Date(b.ev.date || 0));
                    return [ev.id, buildProgressionEvalNat(eleveEntry, ev, bilansFiche)];
                }),
            ),
        [fiches, bilans, selectedEleveId],
    );

    return (
        <div className="px-3 sm:px-4">
            {/* En-tête + retour (cohérent avec DetailEleve) */}
            <div className="flex items-center gap-3 mb-4">
                <button
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all duration-200 hover:-translate-y-0.5 text-xs sm:text-sm"
                >
                    <ArrowLeft size={15} /> RETOUR
                </button>
                <div className="min-w-0">
                    <div className="text-[11px] font-extrabold uppercase tracking-wide text-violet-600">Évaluations nationales</div>
                    <h2 className="text-lg font-extrabold text-slate-800 truncate">{nomEleve}</h2>
                </div>
            </div>

            {isLoading ? (
                <Loading loadingText="Chargement des résultats nationaux…" />
            ) : fiches.length === 0 ? (
                <div className="bg-white border-2 border-[#D5E6F3] rounded-2xl py-14 flex flex-col items-center gap-3 text-center">
                    {classeHors ? (
                        <>
                            <ArrowRightLeft size={36} className="text-gray-300" />
                            <p className="text-sm font-semibold text-gray-600 max-w-md">{phraseHorsBarrette(nomEleve, classeHors)}</p>
                        </>
                    ) : (
                        <>
                            <BarChart3 size={36} className="text-gray-300" />
                            <p className="text-sm font-semibold text-gray-600">Aucun résultat national pour cet élève.</p>
                            <p className="text-xs text-gray-400 max-w-md">
                                Les résultats apparaissent ici une fois l'évaluation nationale importée pour la classe.
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {fiches.map(({ ev, eleveEntry }) => {
                        const domaines = domainesDe(ev, eleveEntry);
                        const progression = progressionParFiche.get(ev.id) || [];
                        return (
                            <div key={ev.id} className="bg-white border-2 border-[#D5E6F3] rounded-2xl shadow-sm p-4 sm:p-5">
                                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-sm font-extrabold text-slate-800">{ev.titre}</h3>
                                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                                matiereKey(ev.matiere) === 'maths'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-orange-100 text-orange-700'
                                            }`}>
                                                {matiereKey(ev.matiere) === 'maths' ? '🔢 Maths' : '📖 Français'}
                                            </span>
                                        </div>
                                        {ev.date ? <p className="text-xs text-gray-400 mt-0.5">{formatLettresDate(ev.date)}</p> : null}
                                    </div>
                                    <button
                                        onClick={() => setDetail({ ev, eleveEntry })}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#EDF6FC] text-blue-600 hover:bg-[#DEEEFA] transition-colors"
                                    >
                                        <Eye size={14} /> Voir le détail
                                    </button>
                                </div>

                                {/* Niveaux par domaine */}
                                {domaines.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {domaines.map((d) => (
                                            <span
                                                key={d.label}
                                                className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl border border-[#E8F3FB] bg-[#F2F8FC]"
                                            >
                                                <span
                                                    className="text-[11px] font-extrabold px-2 py-0.5 rounded-full whitespace-nowrap"
                                                    style={{ background: couleurNiveau(d.niveau), color: texteFonce(d.niveau) ? '#0B2818' : '#fff' }}
                                                >
                                                    {d.niveau}
                                                </span>
                                                <span className="text-xs font-semibold text-slate-600">{d.label}</span>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-400">Niveaux par domaine non renseignés — voir le détail question par question.</p>
                                )}

                                {/* Progression par item : National → ré-évals du prof (lecture verticale = progression) */}
                                {progression.length > 0 ? (
                                    <div className="mt-4 pt-4 border-t border-[#EAF3FA]">
                                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                                            Progression par item
                                        </div>
                                        <div className="space-y-4">
                                            {progression.map((cat) => (
                                                <div key={cat.key}>
                                                    <div className="text-xs font-bold text-slate-600 mb-1.5">{cat.label}</div>
                                                    <div className="overflow-x-auto">
                                                        <table className="border-separate border-spacing-1">
                                                            <thead>
                                                                <tr>
                                                                    <th className="sticky left-0 bg-white z-10" />
                                                                    {cat.colonnes.map((col) => (
                                                                        <th
                                                                            key={col.questionId}
                                                                            title={col.questionId}
                                                                            className="w-5 text-[10px] font-mono font-semibold text-slate-400 align-bottom"
                                                                        >
                                                                            {String(col.questionNum).replace(/^Q/i, '')}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cat.lignes.map((ligne) => (
                                                                    <tr key={ligne.id}>
                                                                        <td className="sticky left-0 bg-white z-10 pr-3 whitespace-nowrap" title={ligne.label}>
                                                                            <span className={`text-[11px] font-bold ${ligne.isNational ? 'text-slate-700' : 'text-slate-500'}`}>
                                                                                {ligne.isNational ? 'National' : ligne.date ? formatLettresDate(ligne.date) : ligne.label}
                                                                            </span>
                                                                            <span className="ml-1.5 text-[10px] font-semibold text-slate-300">
                                                                                {ligne.compteur.reussis}/{ligne.compteur.evalues}
                                                                            </span>
                                                                        </td>
                                                                        {cat.colonnes.map((col) => {
                                                                            const cell = ligne.cellules.get(col.questionId);
                                                                            const couleur = cell ? COULEUR_CARRE[cell.etat] || 'bg-slate-200' : 'bg-slate-100';
                                                                            const tip = cell
                                                                                ? `${col.questionNum} · ${cell.reussis}/${cell.evalues} réussi${cell.reussis > 1 ? 's' : ''}`
                                                                                : `${col.questionNum} · non passé`;
                                                                            return (
                                                                                <td key={col.questionId} className="p-0">
                                                                                    {ligne.isNational ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => setDetail({ ev, eleveEntry, tab: cat.key })}
                                                                                            title={tip}
                                                                                            aria-label={tip}
                                                                                            className={`w-5 h-5 rounded transition-transform hover:scale-110 hover:ring-2 hover:ring-offset-1 hover:ring-blue-300 ${couleur}`}
                                                                                        />
                                                                                    ) : (
                                                                                        <div className={`w-5 h-5 rounded ${couleur}`} title={tip} aria-label={tip} />
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Détail question par question (modal existant) */}
            {detail ? (
                <ResultatEvalNatByEleve
                    show
                    eleve={detail.eleveEntry}
                    EvalNat={detail.ev}
                    initialTab={detail.tab}
                    onClose={() => setDetail(null)}
                />
            ) : null}
        </div>
    );
};

export default EleveEvalNat;
